import React from 'react';
import html2canvas from 'html2canvas';
import { Button } from '../ui/button';
import { Download, Eye, Mail, Printer, Smartphone, Sparkles } from 'lucide-react';
import axios from 'axios';

export const SmilesTab = ({ dashboardData, error, loadSmileTransformations, practice, selectedPatientId, showSmsSelectionModal, smileTransformations, toast, user }) => (
  <>
    <div className="space-y-4">
      {smileTransformations.map((transformation) => {
        const patient = dashboardData?.recentPatients?.find(p => p.id === transformation.patientId);
        const patientName = transformation.patientName || (patient ? `${patient.firstName} ${patient.lastName}` : 'Patient');
        const patientEmail = transformation.patientEmail || patient?.email;
        const patientPhone = patient?.cellPhone || patient?.phone;

        return (
        <div key={transformation.id} className="border rounded-lg p-4 bg-purple-50 border-purple-200 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex gap-2">
                {transformation.beforeImage && (
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Before</p>
                    <img 
                      src={transformation.beforeImage} 
                      alt="Before" 
                      className="w-24 h-24 object-cover rounded border"
                    />
                  </div>
                )}
                {transformation.afterImage && (
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">After</p>
                    <img 
                      src={transformation.afterImage} 
                      alt="After" 
                      className="w-24 h-24 object-cover rounded border border-purple-300"
                    />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-gray-900">Smile Transformation</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    transformation.transformationLevel === 'natural' ? 'bg-emerald-100 text-emerald-700' :
                    transformation.transformationLevel === 'moderate' ? 'bg-blue-100 text-blue-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {transformation.transformationLevel?.charAt(0).toUpperCase() + transformation.transformationLevel?.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  Patient: {patientName}
                </p>
                <p className="text-sm text-gray-500">
                  Created: {new Date(transformation.createdAt).toLocaleDateString()}
                </p>
                {transformation.notes && (
                  <p className="text-sm text-gray-500 mt-1 italic">{transformation.notes}</p>
                )}
                {/* Email/SMS sent indicators */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {transformation.emailSent && (
                    <span className="text-xs text-green-600 flex items-center gap-1 bg-green-50 px-2 py-1 rounded">
                      <Mail className="h-3 w-3" /> 
                      Emailed {transformation.emailSentAt ? new Date(transformation.emailSentAt).toLocaleDateString() : ''}
                    </span>
                  )}
                  {transformation.smsSentAt && (
                    <span className="text-xs text-blue-600 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                      <Smartphone className="h-3 w-3" /> 
                      Texted {new Date(transformation.smsSentAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-1">
              <Button
                data-testid={`view-smile-${transformation.id}`}
                size="sm"
                variant="outline"
                onClick={() => {
                  // Open view in new window with print-ready format
                  const viewWindow = window.open('', '_blank');
                  if (viewWindow) {
                    viewWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Smile Transformation - ${patientName}</title>
                        <style>
                          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: #f3f4f6; }
                          .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #1f2937; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; z-index: 1000; box-shadow: 0 2px 10px rgba(0,0,0,0.2); }
                          .toolbar-title { color: white; font-size: 16px; font-weight: 600; }
                          .toolbar-buttons { display: flex; gap: 10px; }
                          .btn { padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: all 0.2s; }
                          .btn-back { background: #4b5563; color: white; }
                          .btn-back:hover { background: #6b7280; }
                          .btn-print { background: #7c3aed; color: white; }
                          .btn-print:hover { background: #6d28d9; }
                          .container { max-width: 900px; margin: 70px auto 20px; background: white; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); overflow: hidden; }
                          .header { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 30px; text-align: center; }
                          .header h1 { margin: 0 0 10px; font-size: 28px; }
                          .header p { margin: 0; opacity: 0.9; }
                          .practice-info { background: #f8fafc; padding: 20px; text-align: center; border-bottom: 1px solid #e5e7eb; }
                          .practice-logo { max-height: 60px; margin-bottom: 10px; }
                          .practice-name { font-size: 20px; font-weight: bold; color: #1e40af; margin: 5px 0; }
                          .content { padding: 30px; }
                          .patient-info { background: #f0f9ff; padding: 15px 20px; border-radius: 8px; margin-bottom: 25px; }
                          .images { display: flex; justify-content: center; gap: 30px; margin: 25px 0; }
                          .image-container { text-align: center; flex: 1; max-width: 400px; }
                          .image-container img { width: 100%; height: auto; border-radius: 8px; border: 2px solid #e5e7eb; }
                          .image-container.after img { border-color: #7c3aed; }
                          .image-label { font-weight: bold; margin-bottom: 10px; font-size: 16px; text-transform: uppercase; }
                          .before-label { color: #374151; }
                          .after-label { color: #7c3aed; }
                          .disclaimer { background: #fef3c7; border: 1px solid #f59e0b; padding: 15px; border-radius: 8px; margin-top: 25px; }
                          .disclaimer-title { color: #92400e; font-weight: bold; font-size: 12px; margin-bottom: 5px; }
                          .disclaimer-text { font-size: 11px; color: #92400e; line-height: 1.5; margin: 0; }
                          .footer { background: #1f2937; color: #9ca3af; padding: 20px; text-align: center; font-size: 12px; }
                          @media print { 
                            .toolbar { display: none; }
                            body { background: white; padding: 0; }
                            .container { margin-top: 0; box-shadow: none; }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="toolbar">
                          <span class="toolbar-title">Smile Transformation - ${patientName}</span>
                          <div class="toolbar-buttons">
                            <button class="btn btn-print" onclick="window.print()">🖨️ Print</button>
                            <button class="btn btn-back" onclick="window.close()">← Back</button>
                          </div>
                        </div>
                        <div class="container">
                          <div class="header">
                            <h1>✨ Smile Transformation</h1>
                            <p>${transformation.transformationLevel?.charAt(0).toUpperCase() + transformation.transformationLevel?.slice(1)} Enhancement</p>
                          </div>
                          <div class="practice-info">
                            ${practice?.branding?.logo ? `<img src="${practice.branding.logo}" alt="${practice?.name}" class="practice-logo"><br>` : ''}
                            <div class="practice-name">${practice?.name || 'Dental Practice'}</div>
                            <p style="color: #6b7280; font-size: 14px; margin: 5px 0;">
                              ${practice?.phone || ''} ${practice?.address ? `| ${typeof practice.address === 'object' ? [practice.address.street, practice.address.city, practice.address.state, practice.address.zipCode].filter(Boolean).join(', ') : practice.address}` : ''}
                            </p>
                          </div>
                          <div class="content">
                            <div class="patient-info">
                              <strong>Patient:</strong> ${patientName} &nbsp;&nbsp;|&nbsp;&nbsp;
                              <strong>Date:</strong> ${new Date(transformation.createdAt).toLocaleDateString()}
                            </div>
                            <div class="images">
                              <div class="image-container">
                                <p class="image-label before-label">Before</p>
                                <img src="${transformation.beforeImage}" alt="Before" />
                              </div>
                              <div class="image-container after">
                                <p class="image-label after-label">After</p>
                                <img src="${transformation.afterImage}" alt="After" />
                              </div>
                            </div>
                            <div class="disclaimer">
                              <div class="disclaimer-title">⚠️ Important Notice</div>
                              <p class="disclaimer-text">The images shown are digital simulations intended to illustrate potential cosmetic dentistry outcomes. These simulations are for visualization purposes only and do not guarantee specific results. Actual results may vary based on individual factors including oral health, bone structure, and treatment options.</p>
                            </div>
                          </div>
                          <div class="footer">
                            ${practice?.name || 'Dental Practice'}<br>
                            Smile simulation powered by SmileDesign AI
                          </div>
                        </div>
                      </body>
                      </html>
                    `);
                    viewWindow.document.close();
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Eye className="h-3 w-3 mr-1" />
                View
              </Button>
              <Button
                data-testid={`print-smile-${transformation.id}`}
                size="sm"
                variant="outline"
                onClick={() => {
                  // Open print window with full-size images
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    const addressStr = practice?.address ? (typeof practice.address === 'object' ? [practice.address.street, practice.address.city, practice.address.state, practice.address.zipCode].filter(Boolean).join(', ') : practice.address) : '';

                    const printContent = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Smile Transformation - ${patientName}</title>
                        <style>
                          @page { size: letter portrait; margin: 0.25in; }
                          * { box-sizing: border-box; margin: 0; padding: 0; }
                          body { font-family: Arial, sans-serif; color: #333; }
                          .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #1f2937; padding: 10px 20px; display: flex; justify-content: space-between; align-items: center; z-index: 1000; }
                          .toolbar-title { color: white; font-size: 16px; font-weight: 600; }
                          .toolbar-buttons { display: flex; gap: 10px; }
                          .btn { padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; }
                          .btn-back { background: #4b5563; color: white; }
                          .btn-print { background: #7c3aed; color: white; }
                          .page { width: 100%; padding: 8px; margin-top: 50px; }
                          .header { text-align: center; border-bottom: 3px solid #7c3aed; padding-bottom: 6px; margin-bottom: 6px; }
                          .practice-logo { max-height: 50px; margin-bottom: 4px; }
                          .practice-name { font-size: 20px; font-weight: bold; color: #7c3aed; margin: 3px 0; }
                          .practice-info { font-size: 11px; color: #666; }
                          .patient-info { background: #f0f9ff; padding: 6px 12px; border-radius: 4px; margin-bottom: 6px; font-size: 12px; text-align: center; }
                          .title { font-size: 18px; font-weight: bold; color: #7c3aed; text-align: center; margin-bottom: 8px; }
                          .images-container { display: flex; justify-content: center; gap: 20px; }
                          .image-box { text-align: center; width: 48%; }
                          .image-box img { width: 100%; height: auto; object-fit: contain; border: 2px solid #e5e7eb; border-radius: 8px; }
                          .image-box.after img { border-color: #7c3aed; }
                          .image-label { font-weight: bold; font-size: 14px; margin-bottom: 6px; text-transform: uppercase; }
                          .before-label { color: #374151; }
                          .after-label { color: #7c3aed; }
                          .disclaimer { background: #fef3c7; border: 1px solid #f59e0b; padding: 8px; border-radius: 4px; margin-top: 8px; }
                          .disclaimer-title { color: #92400e; font-weight: bold; font-size: 10px; margin-bottom: 3px; }
                          .disclaimer-text { font-size: 8px; color: #92400e; line-height: 1.3; }
                          .footer { text-align: center; margin-top: 6px; padding-top: 5px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #9ca3af; }
                          @media print { 
                            .toolbar { display: none !important; }
                            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                            .page { margin-top: 0; padding: 0; }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="toolbar">
                          <span class="toolbar-title">Print Preview - ${patientName}</span>
                          <div class="toolbar-buttons">
                            <button class="btn btn-print" onclick="window.print()">🖨️ Print Now</button>
                            <button class="btn btn-back" onclick="window.close()">← Back</button>
                          </div>
                        </div>
                        <div class="page">
                          <div class="header">
                            ${practice?.branding?.logo ? `<img src="${practice.branding.logo}" alt="${practice?.name}" class="practice-logo"><br>` : ''}
                            <div class="practice-name">${practice?.name || 'Dental Practice'}</div>
                            <div class="practice-info">${practice?.phone || ''} ${addressStr ? `| ${addressStr}` : ''}</div>
                          </div>
                          <div class="patient-info">
                            <strong>Patient:</strong> ${patientName} &nbsp;|&nbsp;
                            <strong>Date:</strong> ${new Date(transformation.createdAt).toLocaleDateString()} &nbsp;|&nbsp;
                            <strong>Level:</strong> ${transformation.transformationLevel?.charAt(0).toUpperCase() + transformation.transformationLevel?.slice(1)}
                          </div>
                          <div class="title">Smile Transformation</div>
                          <div class="images-container">
                            <div class="image-box">
                              <p class="image-label before-label">Before</p>
                              <img src="${transformation.beforeImage}" alt="Before" />
                            </div>
                            <div class="image-box after">
                              <p class="image-label after-label">After</p>
                              <img src="${transformation.afterImage}" alt="After" />
                            </div>
                          </div>
                          <div class="disclaimer">
                            <div class="disclaimer-title">Important Notice</div>
                            <p class="disclaimer-text">The images shown are digital simulations intended to illustrate potential cosmetic dentistry outcomes. These simulations are for visualization purposes only and do not guarantee specific results.</p>
                          </div>
                          <div class="footer">${practice?.name || 'Dental Practice'} | Smile simulation powered by SmileDesign AI</div>
                        </div>
                      </body>
                      </html>
                    `;
                    printWindow.document.write(printContent);
                    printWindow.document.close();
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Printer className="h-3 w-3 mr-1" />
                Print
              </Button>
              <Button
                data-testid={`email-smile-${transformation.id}`}
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!patientEmail) {
                    toast({
                      title: "No Email Address",
                      description: "This patient does not have an email address on file",
                      variant: "destructive",
                    });
                    return;
                  }

                  try {
                    toast({
                      title: "Sending Email",
                      description: `Sending smile transformation to ${patientEmail}...`,
                    });

                    const token = localStorage.getItem('dentalToken');

                    // Format address if it's an object
                    let addressStr = '';
                    if (practice?.address) {
                      if (typeof practice.address === 'string') {
                        addressStr = practice.address;
                      } else if (typeof practice.address === 'object') {
                        const addr = practice.address;
                        addressStr = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');
                      }
                    }

                    await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/smile-design/send-to-patient`,
                      {
                        transformationId: transformation.id,
                        patientId: transformation.patientId,
                        patientName: patientName,
                        patientEmail: patientEmail,
                        beforeImage: transformation.beforeImage,
                        afterImage: transformation.afterImage,
                        transformationLevel: transformation.transformationLevel || 'natural',
                        practiceId: practice?.id || null,
                        practiceName: practice?.name || 'Dental Practice',
                        practiceEmail: practice?.email || user?.email || null,
                        practicePhone: practice?.phone || practice?.emergencyContact || '',
                        practiceAddress: addressStr,
                        practiceLogo: practice?.branding?.logo || practice?.logo || ''
                      },
                      {
                        headers: { Authorization: `Bearer ${token}` }
                      }
                    );

                    toast({
                      title: "✅ Email Sent Successfully",
                      description: `Smile transformation sent to ${patientEmail}`,
                    });

                    // Refresh to show updated status
                    loadSmileTransformations(selectedPatientId);
                  } catch (error) {
                    console.error('Email error:', error);
                    toast({
                      title: "Email Failed",
                      description: error.response?.data?.detail || "Failed to send email",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Mail className="h-3 w-3 mr-1" />
                Email
              </Button>
              <Button
                data-testid={`text-smile-${transformation.id}`}
                size="sm"
                variant="outline"
                onClick={() => {
                  showSmsSelectionModal(
                    transformation.patientId,
                    'smile',
                    transformation.id,
                    patientPhone,
                    patientName,
                    'Smile Transformation'
                  );
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Smartphone className="h-3 w-3 mr-1" />
                Text
              </Button>
              <Button
                data-testid={`download-smile-${transformation.id}`}
                size="sm"
                variant="outline"
                onClick={async () => {
                  // Download as PNG using html2canvas
                  const fileName = `Smile_Transformation_${patientName.replace(/\s+/g, '_')}_${new Date(transformation.createdAt).toISOString().split('T')[0]}.png`;

                  toast({
                    title: "Generating PNG",
                    description: "Please wait...",
                  });

                  try {
                    const addressStr = practice?.address ? (typeof practice.address === 'object' ? [practice.address.street, practice.address.city, practice.address.state, practice.address.zipCode].filter(Boolean).join(', ') : practice.address) : '';

                    // Create hidden container for rendering
                    const container = document.createElement('div');
                    container.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 1200px; background: white;';
                    container.innerHTML = `
                      <div style="font-family: Arial, sans-serif; padding: 40px; background: white;">
                        <div style="text-align: center; border-bottom: 4px solid #7c3aed; padding-bottom: 20px; margin-bottom: 25px;">
                          ${practice?.branding?.logo ? `<img src="${practice.branding.logo}" alt="${practice?.name}" style="max-height: 80px; margin-bottom: 10px;" crossorigin="anonymous"><br>` : ''}
                          <div style="font-size: 28px; font-weight: bold; color: #7c3aed;">${practice?.name || 'Dental Practice'}</div>
                          <div style="font-size: 14px; color: #666; margin-top: 5px;">${practice?.phone || ''}${addressStr ? ` | ${addressStr}` : ''}</div>
                        </div>
                        <div style="background: #f0f9ff; padding: 15px 20px; border-radius: 10px; margin-bottom: 25px; text-align: center; font-size: 16px;">
                          <strong>Patient:</strong> ${patientName} &nbsp;&nbsp;|&nbsp;&nbsp;
                          <strong>Date:</strong> ${new Date(transformation.createdAt).toLocaleDateString()} &nbsp;&nbsp;|&nbsp;&nbsp;
                          <strong>Level:</strong> ${transformation.transformationLevel?.charAt(0).toUpperCase() + transformation.transformationLevel?.slice(1)}
                        </div>
                        <div style="font-size: 26px; font-weight: bold; color: #7c3aed; text-align: center; margin-bottom: 25px;">Smile Transformation</div>
                        <div style="display: flex; justify-content: center; gap: 30px; margin-bottom: 30px;">
                          <div style="text-align: center; flex: 1; max-width: 500px;">
                            <p style="font-weight: bold; font-size: 18px; color: #374151; margin-bottom: 10px; text-transform: uppercase;">Before</p>
                            <img src="${transformation.beforeImage}" alt="Before" style="width: 100%; height: auto; border: 3px solid #e5e7eb; border-radius: 12px;" crossorigin="anonymous" />
                          </div>
                          <div style="text-align: center; flex: 1; max-width: 500px;">
                            <p style="font-weight: bold; font-size: 18px; color: #7c3aed; margin-bottom: 10px; text-transform: uppercase;">After</p>
                            <img src="${transformation.afterImage}" alt="After" style="width: 100%; height: auto; border: 3px solid #7c3aed; border-radius: 12px;" crossorigin="anonymous" />
                          </div>
                        </div>
                        <div style="background: #fef3c7; border: 2px solid #f59e0b; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
                          <div style="color: #92400e; font-weight: bold; font-size: 14px; margin-bottom: 8px;">Important Notice</div>
                          <p style="font-size: 12px; color: #92400e; line-height: 1.5; margin: 0;">The images shown are digital simulations intended to illustrate potential cosmetic dentistry outcomes. These simulations are for visualization purposes only and do not guarantee specific results.</p>
                        </div>
                        <div style="text-align: center; padding-top: 15px; border-top: 2px solid #e5e7eb; font-size: 12px; color: #9ca3af;">
                          ${practice?.name || 'Dental Practice'} | Smile simulation powered by SmileDesign AI
                        </div>
                      </div>
                    `;

                    document.body.appendChild(container);

                    // Wait for images to load
                    const images = container.querySelectorAll('img');
                    await Promise.all(Array.from(images).map(img => {
                      if (img.complete) return Promise.resolve();
                      return new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve;
                      });
                    }));

                    // Use html2canvas to create PNG
                    const canvas = await html2canvas(container, {
                      scale: 2,
                      useCORS: true,
                      allowTaint: true,
                      backgroundColor: '#ffffff',
                      logging: false
                    });

                    // Remove the container
                    document.body.removeChild(container);

                    // Download as PNG
                    const link = document.createElement('a');
                    link.download = fileName;
                    link.href = canvas.toDataURL('image/png');
                    link.click();

                    toast({
                      title: "Downloaded",
                      description: "PNG saved successfully!",
                    });
                  } catch (error) {
                    console.error('Download error:', error);
                    toast({
                      title: "Download Failed",
                      description: "Could not generate PNG",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </div>
      );
      })}
    </div>
  </>
);
